import { useState, useEffect, useMemo } from 'react';
import { toast } from '@/components/ui/sonner';
import {
  Mail, Send, Settings, History, FileText, Search, Filter,
  Edit2, Save, X, AlertTriangle,
  Calendar, RefreshCw, Eye, Download, Copy, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PlanningEmailRecovery } from '@/components/PlanningEmailRecovery';

// Template categories with colors
const TEMPLATE_CATEGORIES = {
  general: { label: 'Général', color: 'bg-gray-100 text-gray-700', icon: 'G' },
  trial: { label: 'Période d\'essai', color: 'bg-blue-100 text-blue-700', icon: 'P' },
  reminder: { label: 'Rappels', color: 'bg-yellow-100 text-yellow-700', icon: 'R' },
  training: { label: 'Formation', color: 'bg-green-100 text-green-700', icon: 'F' },
  warning: { label: 'Avertissements', color: 'bg-red-100 text-red-700', icon: 'A' },
  summons: { label: 'Convocations', color: 'bg-purple-100 text-purple-700', icon: 'C' },
  vehicle: { label: 'Véhicules', color: 'bg-orange-100 text-orange-700', icon: 'V' },
  progressive: { label: 'Rappels progressifs', color: 'bg-amber-100 text-amber-700', icon: 'RP' },
};

// Email status badges
const STATUS_CONFIG = {
  sent: { label: 'Envoyé', color: 'bg-green-100 text-green-700' },
  delivered: { label: 'Livré', color: 'bg-emerald-100 text-emerald-700' },
  simulated: { label: 'Simulé', color: 'bg-blue-100 text-blue-700' },
  failed: { label: 'Échec', color: 'bg-red-100 text-red-700' },
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  rejected: { label: 'Rejeté', color: 'bg-orange-100 text-orange-700' },
  ignored: { label: 'Ignoré', color: 'bg-slate-100 text-slate-700' },
  cancelled: { label: 'Annulé', color: 'bg-gray-100 text-gray-700' },
  unknown: { label: 'Statut inconnu', color: 'bg-zinc-100 text-zinc-700' },
};

const AdminEmails = ({ user, activeModule = "b2b" }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('templates'); // templates, history, settings, incidents
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // History state
  const [emailHistory, setEmailHistory] = useState([]);
  const [historyData, setHistoryData] = useState({
    counters: {},
    pagination: { page: 1, page_size: 50, total_results: 0, total_pages: 1, result_start: 0, result_end: 0 },
    rows: [],
    filter_options: { sites: [], categories: [], templates: [], statuses: [], dispatch_modes: [], week_starts: [], triggers: [], manual_admins: [] },
    active_filters_count: 0,
    period: { start_date: '', end_date: '' }
  });
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyFilter, setHistoryFilter] = useState({
    search: '',
    employee_name: '',
    first_name: '',
    last_name: '',
    recipient_email: '',
    site_id: '',
    category: '',
    template: '',
    status: 'all',
    dispatch_mode: 'all',
    week_start: '',
    trigger: '',
    manual_admin: '',
    has_error: 'all'
  });
  const [historyPeriod, setHistoryPeriod] = useState({ preset: '30d', start_date: '', end_date: '' });
  const [historyPagination, setHistoryPagination] = useState({ page: 1, page_size: 50 });
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loadingEmailDetail, setLoadingEmailDetail] = useState(false);
  
  // Email config
  const [emailConfig, setEmailConfig] = useState(null);
  const [automationStatusSummary, setAutomationStatusSummary] = useState(null);
  const [automationLoading, setAutomationLoading] = useState({ welcome: false, reminder: false });
  const [automationResult, setAutomationResult] = useState(null);
  const [realSendEmail, setRealSendEmail] = useState('');
  const [realSendMode, setRealSendMode] = useState({ welcome: false, reminder: false });
  const [validationMode, setValidationMode] = useState({ running: false, type: 'both', userId: '' });
  const [validationResult, setValidationResult] = useState(null);
  const [incidentsData, setIncidentsData] = useState({ summary: null, records: [] });
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [incidentFilter, setIncidentFilter] = useState({ incidentType: 'all', emailType: 'all' });
  const [reactivationProcedure, setReactivationProcedure] = useState(null);
  const [readonlyRecipientsLoading, setReadonlyRecipientsLoading] = useState(false);
  const [readonlyRecipientsError, setReadonlyRecipientsError] = useState('');
  const [readonlyRecipientsResult, setReadonlyRecipientsResult] = useState(null);
  const [planningAuditLoading, setPlanningAuditLoading] = useState(false);
  const [planningAuditError, setPlanningAuditError] = useState('');
  const [planningAuditResult, setPlanningAuditResult] = useState(null);
  const [planningAuditForm, setPlanningAuditForm] = useState({
    week_start: (() => { const d = new Date(); d.setDate(d.getDate() + (8 - d.getDay()) % 7); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
    include_manual_trigger: true,
    reported_expected_count: '',
    reported_received_count: ''
  });
  const realSendEnabled = emailConfig?.real_send_enabled === true;
  
  // Employees for send tab
  const [employees, setEmployees] = useState([]);

  // Load data on mount
  useEffect(() => {
    fetchTemplates();
    fetchEmailHistory({ page: 1 });
    fetchEmailConfig();
    fetchAutomationStatusSummary();
    fetchEmployees();
    fetchIncidents();
    fetchReactivationProcedure();
  }, []);

  const fetchTemplates = async () => {
    setTemplateLoading(true);
    try {
      const res = await axios.get(`${API}/email-templates`, { withCredentials: true });
      setTemplates(res.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setTemplateLoading(false);
    }
  };

  const fetchEmailHistory = async (overrides = {}) => {
    setHistoryLoading(true);
    try {
      const targetPage = overrides.page ?? historyPagination.page;
      const targetPageSize = overrides.page_size ?? historyPagination.page_size;
      const targetPeriod = {
        start_date: overrides.start_date ?? historyPeriod.start_date,
        end_date: overrides.end_date ?? historyPeriod.end_date
      };

      const params = new URLSearchParams({
        page: String(targetPage),
        page_size: String(targetPageSize)
      });

      if (targetPeriod.start_date) params.append('start_date', targetPeriod.start_date);
      if (targetPeriod.end_date) params.append('end_date', targetPeriod.end_date);

      Object.entries(historyFilter).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, String(value));
        }
      });

      const res = await axios.get(`${API}/emails/history/advanced?${params.toString()}`, { withCredentials: true });
      const payload = res.data || {};
      setHistoryData(payload);
      setEmailHistory(payload.rows || []);
      setHistoryPagination({
        page: payload.pagination?.page || targetPage,
        page_size: payload.pagination?.page_size || targetPageSize
      });
      if (payload.period?.start_date || payload.period?.end_date) {
        setHistoryPeriod(prev => ({
          ...prev,
          start_date: payload.period?.start_date || prev.start_date,
          end_date: payload.period?.end_date || prev.end_date
        }));
      }
    } catch (error) {
      console.error('Failed to fetch email history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchEmailDetail = async (emailId) => {
    setLoadingEmailDetail(true);
    try {
      const res = await axios.get(`${API}/emails/history/advanced/detail/${emailId}`, { withCredentials: true });
      setSelectedEmail(res.data);
    } catch (error) {
      console.error('Failed to fetch email detail:', error);
      alert('Impossible de charger le détail de cet email');
    } finally {
      setLoadingEmailDetail(false);
    }
  };

  const fetchEmailConfig = async () => {
    try {
      const res = await axios.get(`${API}/emails/config`, { withCredentials: true });
      setEmailConfig(res.data);
    } catch (error) {
      console.error('Failed to fetch email config:', error);
    }
  };

  const fetchAutomationStatusSummary = async () => {
    try {
      const res = await axios.get(`${API}/emails/automation/status-summary`, { withCredentials: true });
      setAutomationStatusSummary(res.data || null);
    } catch (error) {
      console.error('Failed to fetch automation status summary:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      const emps = (res.data || []).filter(u => u.role === 'employee');
      setEmployees(emps);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchIncidents = async () => {
    setIncidentsLoading(true);
    try {
      const params = new URLSearchParams();
      if (incidentFilter.incidentType !== 'all') params.append('incident_type', incidentFilter.incidentType);
      if (incidentFilter.emailType !== 'all') params.append('email_type', incidentFilter.emailType);

      const url = `${API}/emails/automation/incidents${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axios.get(url, { withCredentials: true });
      setIncidentsData({ summary: res.data?.summary || null, records: res.data?.records || [] });
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
    } finally {
      setIncidentsLoading(false);
    }
  };

  const fetchReactivationProcedure = async () => {
    try {
      const res = await axios.get(`${API}/emails/automation/reactivation/procedure`, { withCredentials: true });
      setReactivationProcedure(res.data || null);
    } catch (error) {
      console.error('Failed to fetch reactivation procedure:', error);
    }
  };

  const runAutomation = async (type, runReal = false) => {
    const endpoint = type === 'welcome'
      ? `${API}/emails/automation/welcome/run-now`
      : `${API}/emails/automation/weekly-reminder/run-now`;

    const payload = {
      run_real_send: runReal,
      ...(runReal && realSendEmail ? { test_recipient_email: realSendEmail.trim().toLowerCase() } : {})
    };

    if (runReal && !realSendEmail.trim()) {
      alert('Veuillez saisir une adresse e-mail de test pour un envoi réel contrôlé.');
      return;
    }

    if (runReal && !realSendEnabled) {
      alert('Les envois réels sont temporairement désactivés.');
      return;
    }

    const key = type === 'welcome' ? 'welcome' : 'reminder';
    setAutomationLoading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await axios.post(endpoint, payload, { withCredentials: true });
      setAutomationResult({ type, ...(res.data || {}) });
      await fetchEmailHistory();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Erreur inconnue';
      alert(`Échec exécution ${type === 'welcome' ? 'Bienvenue' : 'Rappel'}: ${detail}`);
    } finally {
      setAutomationLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const runValidationCompare = async () => {
    if (!realSendEmail.trim()) {
      alert('Veuillez saisir une adresse e-mail de test pour le mode validation.');
      return;
    }

    setValidationMode(prev => ({ ...prev, running: true }));
    try {
      const payload = {
        test_recipient_email: realSendEmail.trim().toLowerCase(),
        automation_type: validationMode.type,
        ...(validationMode.userId ? { only_user_id: validationMode.userId } : {})
      };

      const res = await axios.post(
        `${API}/emails/automation/validation/compare`,
        payload,
        { withCredentials: true }
      );

      setValidationResult(res.data || null);
      await fetchEmailHistory();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Erreur inconnue';
      alert(`Échec mode validation: ${detail}`);
    } finally {
      setValidationMode(prev => ({ ...prev, running: false }));
    }
  };

  const refreshReadonlyRecipients = async () => {
    setReadonlyRecipientsLoading(true);
    setReadonlyRecipientsError('');
    try {
      const res = await axios.post(
        `${API}/emails/automation/recipients/read-only-refresh`,
        { max_rows_per_section: 200 },
        { withCredentials: true }
      );
      setReadonlyRecipientsResult(res.data || null);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Erreur inconnue';
      setReadonlyRecipientsError(`Impossible d’actualiser les destinataires: ${detail}`);
    } finally {
      setReadonlyRecipientsLoading(false);
    }
  };

  const runPlanningEmailReadonlyAudit = async () => {
    if (!planningAuditForm.week_start) {
      setPlanningAuditError('Veuillez sélectionner une semaine à analyser.');
      return;
    }

    const selected = new Date(`${planningAuditForm.week_start}T00:00:00`);
    const day = (selected.getDay() + 6) % 7;
    const monday = new Date(selected);
    monday.setDate(selected.getDate() - day);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const mondayIso = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
    const mondayFr = monday.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const sundayFr = sunday.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const confirmed = window.confirm(`Confirmer le diagnostic lecture seule pour la semaine du ${mondayFr} au ${sundayFr} ?`);
    if (!confirmed) return;

    setPlanningAuditLoading(true);
    setPlanningAuditError('');
    try {
      const payload = {
        week_start: mondayIso,
        include_manual_trigger: planningAuditForm.include_manual_trigger,
        max_rows: 500
      };

      const res = await axios.post(
        `${API}/emails/planning/audit-read-only`,
        payload,
        { withCredentials: true }
      );
      setPlanningAuditResult(res.data || null);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Erreur inconnue';
      setPlanningAuditError(`Diagnostic planning impossible: ${detail}`);
    } finally {
      setPlanningAuditLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidents();
  }, [incidentFilter.incidentType, incidentFilter.emailType]);

  const applyHistoryFilters = () => {
    fetchEmailHistory({ page: 1 });
  };

  const resetHistoryFilters = () => {
    setHistoryFilter({
      search: '',
      employee_name: '',
      first_name: '',
      last_name: '',
      recipient_email: '',
      site_id: '',
      category: '',
      template: '',
      status: 'all',
      dispatch_mode: 'all',
      week_start: '',
      trigger: '',
      manual_admin: '',
      has_error: 'all'
    });
    setHistoryPeriod({ preset: '30d', start_date: '', end_date: '' });
    setTimeout(() => fetchEmailHistory({ page: 1, start_date: '', end_date: '' }), 0);
  };

  const setQuickPeriod = (preset) => {
    const today = new Date();
    const toIsoDate = (d) => d.toISOString().slice(0, 10);

    let start = new Date(today);
    let end = new Date(today);

    if (preset === '7d') {
      start.setDate(today.getDate() - 6);
    } else if (preset === '30d') {
      start.setDate(today.getDate() - 29);
    } else if (preset === 'current_month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
    } else if (preset === 'previous_month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (preset === '3m') {
      start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
      start.setDate(start.getDate() + 1);
    } else if (preset === '6m') {
      start = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
      start.setDate(start.getDate() + 1);
    }

    const startIso = toIsoDate(start);
    const endIso = toIsoDate(end);

    setHistoryPeriod({ preset, start_date: startIso, end_date: endIso });
    setTimeout(() => fetchEmailHistory({ page: 1, start_date: startIso, end_date: endIso }), 0);
  };

  const exportHistory = async (exportType) => {
    try {
      const params = new URLSearchParams({ export_type: exportType });
      if (historyPeriod.start_date) params.append('start_date', historyPeriod.start_date);
      if (historyPeriod.end_date) params.append('end_date', historyPeriod.end_date);
      Object.entries(historyFilter).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, String(value));
      });

      const res = await axios.get(`${API}/emails/history/advanced/export?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportType === 'tentatives' ? 'emails_tentatives.csv' : 'emails_messages.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert('Export impossible pour le moment.');
    }
  };

  const copyPlanningAuditJson = async () => {
    if (!planningAuditResult) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(planningAuditResult, null, 2));
      alert('Rapport JSON copié dans le presse-papiers.');
    } catch (error) {
      alert('Impossible de copier automatiquement.');
    }
  };

  // Save template
  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/email-templates/${editingTemplate.template_key}`, {
        name: editingTemplate.name,
        subject: editingTemplate.subject,
        content: editingTemplate.content,
        description: editingTemplate.description,
        is_active: editingTemplate.is_active !== false
      }, { withCredentials: true });
      
      await fetchTemplates();
      setEditingTemplate(null);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Reset template to default
  const handleResetTemplate = async (templateKey) => {
    if (!confirm('Réinitialiser ce template aux valeurs par défaut ?')) return;
    
    try {
      await axios.delete(`${API}/email-templates/${templateKey}`, { withCredentials: true });
      await fetchTemplates();
      setSelectedTemplate(null);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Failed to reset template:', error);
      toast.error(`Modèle non réinitialisé : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getWeekRangeLabel = (selectedDate) => {
    if (!selectedDate) return 'Semaine non sélectionnée';
    const date = new Date(`${selectedDate}T00:00:00`);
    const day = (date.getDay() + 6) % 7;
    const monday = new Date(date);
    monday.setDate(date.getDate() - day);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const start = monday.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    const end = sunday.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    return `Semaine du ${start} au ${end}`;
  };

  const environmentLabel = useMemo(() => {
    const host = window.location.hostname || '';
    const isPreviewHost = host.includes('preview.emergentagent.com') || host.includes('localhost') || host.includes('127.0.0.1');
    return isPreviewHost ? 'Preview' : 'Production';
  }, []);

  const isProductionEnvironment = environmentLabel === 'Production';
  const getAutomationStateLabel = (isActive) => (isActive ? 'Actif' : 'Inactif');

  // Get category for template
  const getTemplateCategory = (templateKey) => {
    if (templateKey.startsWith('warning_')) return 'warning';
    if (templateKey.startsWith('reminder_')) return 'progressive';
    if (templateKey.includes('training') || templateKey.includes('binome')) return 'training';
    if (templateKey.includes('trial')) return 'trial';
    if (templateKey.includes('vehicle') || templateKey.includes('whatsapp')) return 'vehicle';
    if (templateKey === 'summons_phone') return 'summons';
    if (templateKey === 'mission_not_validated' || templateKey === 'profile_incomplete') return 'reminder';
    return 'general';
  };

  return (
    <Layout user={user} title="Gestion des Emails">
      <div className="space-y-6">
        {/* Header with config status */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gestion des Emails</h1>
            <p className="text-sm text-gray-500">
              Templates, historique et envoi centralisé
            </p>
          </div>
          <div className="flex items-center gap-4">
            {emailConfig && (
              <div data-testid="email-config-status-badge" className={`px-3 py-1 rounded-full text-sm ${
                emailConfig.email_test_mode
                  ? 'bg-amber-100 text-amber-800'
                  : emailConfig.configured
                    ? 'bg-green-100 text-green-700'
                    : 'bg-yellow-100 text-yellow-700'
              }`}>
                {emailConfig.email_test_mode
                  ? 'Mode test (Preview) — envois interceptés'
                  : emailConfig.configured ? 'API Resend active' : 'Mode simulation'}
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => { fetchTemplates(); fetchEmailHistory(); fetchEmailConfig(); fetchAutomationStatusSummary(); fetchIncidents(); fetchReactivationProcedure(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'templates'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Modèles ({templates.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            data-testid="admin-emails-history-tab"
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <History className="w-4 h-4 inline mr-2" />
            Historique ({historyData.pagination?.total_results ?? emailHistory.length})
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'settings'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Paramètres
          </button>
          <button
            data-testid="email-incidents-tab-button"
            onClick={() => setActiveTab('incidents')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'incidents'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Incidents e-mails
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'templates' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Templates List */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Modèles d&apos;emails</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {templateLoading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : (
                    <div className="divide-y max-h-[600px] overflow-y-auto">
                      {Object.entries(TEMPLATE_CATEGORIES).map(([catKey, cat]) => {
                        const catTemplates = templates.filter(t => getTemplateCategory(t.template_key) === catKey);
                        if (catTemplates.length === 0) return null;
                        
                        return (
                          <div key={catKey}>
                            <div className={`px-4 py-2 text-xs font-semibold ${cat.color}`}>
                              {cat.icon} {cat.label} ({catTemplates.length})
                            </div>
                            {catTemplates.map(template => (
                              <button
                                key={template.template_key}
                                onClick={() => {
                                  setSelectedTemplate(template);
                                  setEditingTemplate(null);
                                }}
                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                                  selectedTemplate?.template_key === template.template_key
                                    ? 'bg-blue-50 border-l-4 border-blue-600'
                                    : ''
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm text-gray-800">
                                    {template.name || template.trigger_name}
                                  </span>
                                  {template.is_customized && (
                                    <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                      Modifié
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 truncate mt-0.5">
                                  {template.description || template.template_key}
                                </p>
                              </button>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Template Editor */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {editingTemplate ? 'Édition du modèle' : 'Aperçu du modèle'}
                    </CardTitle>
                    {selectedTemplate && !editingTemplate && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => setEditingTemplate({ ...selectedTemplate })}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Modifier
                        </Button>
                        {selectedTemplate.is_customized && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResetTemplate(selectedTemplate.template_key)}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Réinitialiser
                          </Button>
                        )}
                      </div>
                    )}
                    {editingTemplate && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveTemplate}
                          disabled={savingTemplate}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Save className="w-4 h-4 mr-1" />
                          {savingTemplate ? 'Sauvegarde...' : 'Sauvegarder'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingTemplate(null)}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Annuler
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!selectedTemplate && !editingTemplate ? (
                    <div className="text-center py-12 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Sélectionnez un modèle pour le visualiser ou le modifier</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Template Key (read-only) */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Identifiant technique
                        </label>
                        <div className="px-3 py-2 bg-gray-100 rounded text-sm font-mono text-gray-600">
                          {(editingTemplate || selectedTemplate).template_key}
                        </div>
                      </div>

                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nom du modèle
                        </label>
                        {editingTemplate ? (
                          <Input
                            value={editingTemplate.name || ''}
                            onChange={(e) => setEditingTemplate({
                              ...editingTemplate,
                              name: e.target.value
                            })}
                            placeholder="Nom du modèle"
                          />
                        ) : (
                          <p className="text-gray-800">{selectedTemplate.name || selectedTemplate.trigger_name}</p>
                        )}
                      </div>

                      {/* Subject */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Objet de l&apos;email
                        </label>
                        {editingTemplate ? (
                          <Input
                            value={editingTemplate.subject || ''}
                            onChange={(e) => setEditingTemplate({
                              ...editingTemplate,
                              subject: e.target.value
                            })}
                            placeholder="Objet de l'email"
                          />
                        ) : (
                          <p className="text-gray-800">{selectedTemplate.subject || '-'}</p>
                        )}
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        {editingTemplate ? (
                          <Input
                            value={editingTemplate.description || ''}
                            onChange={(e) => setEditingTemplate({
                              ...editingTemplate,
                              description: e.target.value
                            })}
                            placeholder="Description du modèle"
                          />
                        ) : (
                          <p className="text-gray-600 text-sm">{selectedTemplate.description || '-'}</p>
                        )}
                      </div>

                      {/* Content */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contenu de l&apos;email
                        </label>
                        {editingTemplate ? (
                          <textarea
                            value={editingTemplate.content || ''}
                            onChange={(e) => setEditingTemplate({
                              ...editingTemplate,
                              content: e.target.value
                            })}
                            rows={10}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Contenu du modèle (utilisez {first_name}, {date}, etc. pour les variables)"
                          />
                        ) : (
                          <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                            {selectedTemplate.content || 'Contenu par défaut (non modifié)'}
                          </div>
                        )}
                      </div>

                      {/* Variables */}
                      {(selectedTemplate?.variables || editingTemplate?.variables) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Variables disponibles
                          </label>
                          <div className="flex flex-wrap gap-1">
                            {(editingTemplate?.variables || selectedTemplate.variables || []).map(v => (
                              <span
                                key={v}
                                className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-mono cursor-pointer hover:bg-blue-200"
                                onClick={() => {
                                  if (editingTemplate) {
                                    setEditingTemplate({
                                      ...editingTemplate,
                                      content: (editingTemplate.content || '') + `{${v}}`
                                    });
                                  }
                                }}
                                title="Cliquez pour insérer"
                              >
                                {`{${v}}`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-6" data-testid="emails-history-advanced-page">
            {(user?.is_super_admin || user?.admin_level === 'full') && <PlanningEmailRecovery />}
            <Card className="border-emerald-200" data-testid="planning-email-diagnostic-card">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg" data-testid="planning-diagnostic-title">Diagnostic des e-mails du planning</CardTitle>
                    <p className="text-xs text-slate-700 mt-1" data-testid="planning-diagnostic-readonly-note">
                      Ce diagnostic n&apos;envoie aucun e-mail et ne modifie aucune donnée.
                    </p>
                  </div>
                  <div className="flex items-end gap-2">
                    <div>
                      <label className="text-xs text-slate-600" data-testid="planning-week-picker-label">Semaine à analyser</label>
                      <Input
                        data-testid="planning-week-calendar-input"
                        type="date"
                        value={planningAuditForm.week_start}
                        onChange={(e) => setPlanningAuditForm(prev => ({ ...prev, week_start: e.target.value }))}
                        className="w-[220px]"
                      />
                    </div>
                    <Button
                      data-testid="launch-planning-diagnostic-readonly-button"
                      variant="outline"
                      onClick={runPlanningEmailReadonlyAudit}
                      disabled={planningAuditLoading}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${planningAuditLoading ? 'animate-spin' : ''}`} />
                      {planningAuditLoading ? 'Diagnostic en cours...' : 'Lancer le diagnostic en lecture seule'}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-slate-600" data-testid="planning-week-human-label">
                  {getWeekRangeLabel(planningAuditForm.week_start)}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {planningAuditError && (
                  <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-xs" data-testid="planning-audit-error-banner">
                    {planningAuditError}
                  </div>
                )}

                {planningAuditResult && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 text-xs" data-testid="planning-audit-executive-kpis">
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-active-approved">Actifs approuvés: <strong>{planningAuditResult.executive_summary?.salaries_actifs_approuves ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-valid-email">E-mails valides: <strong>{planningAuditResult.executive_summary?.salaries_email_valide ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-with-schedule">Avec planning: <strong>{planningAuditResult.executive_summary?.salaries_avec_planning ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-without-schedule">Sans planning: <strong>{planningAuditResult.executive_summary?.salaries_sans_planning ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-sent">Envoyés réels: <strong>{planningAuditResult.executive_summary?.emails_reellement_envoyes ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-delivered">Livrés confirmés: <strong>{planningAuditResult.executive_summary?.emails_livres_confirmes ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-pending">En attente: <strong>{planningAuditResult.executive_summary?.envois_en_attente ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-failed">Échecs: <strong>{planningAuditResult.executive_summary?.echecs ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-rejected">Rejets: <strong>{planningAuditResult.executive_summary?.rejets ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-simulated">Simulations: <strong>{planningAuditResult.executive_summary?.simulations ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-no-attempt">Aucune tentative: <strong>{planningAuditResult.executive_summary?.aucune_tentative ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-legacy">Ancienne règle: <strong>{planningAuditResult.executive_summary?.exclusions_ancienne_regle ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-manual-review">Vérification manuelle: <strong>{planningAuditResult.executive_summary?.verification_manuelle ?? 0}</strong></div>
                      <div className="p-2 rounded border bg-white" data-testid="diag-kpi-catchup">Rattrapage possible: <strong>{planningAuditResult.executive_summary?.rattrapage_potentiel ?? 0}</strong></div>
                    </div>

                    <div className="flex justify-end">
                      <Button data-testid="copy-planning-diagnostic-json-button" variant="outline" size="sm" onClick={copyPlanningAuditJson}>
                        <Copy className="w-4 h-4 mr-2" />
                        Copier le rapport JSON
                      </Button>
                    </div>

                    <div className="rounded-lg border overflow-auto" data-testid="planning-diagnostic-missing-table">
                      <table className="w-full text-xs min-w-[1080px]">
                        <thead className="bg-slate-50 border-b">
                          <tr>
                            <th className="text-left p-2">Nom et prénom</th>
                            <th className="text-left p-2">E-mail masqué</th>
                            <th className="text-left p-2">Site</th>
                            <th className="text-left p-2">Planning</th>
                            <th className="text-left p-2">Modèle prévu</th>
                            <th className="text-left p-2">Statut réel</th>
                            <th className="text-left p-2">Raison</th>
                            <th className="text-left p-2">Tentative</th>
                            <th className="text-left p-2">Vérification manuelle</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(planningAuditResult.missing_recipients_table || []).map((row, idx) => (
                            <tr key={`${row.user_id || 'missing'}-${idx}`} className="border-b last:border-b-0" data-testid={`planning-missing-row-${idx}`}>
                              <td className="p-2">{row.employee_name || '-'}</td>
                              <td className="p-2">{row.masked_email || '-'}</td>
                              <td className="p-2">{row.site || '-'}</td>
                              <td className="p-2">{row.has_schedule ? 'Avec planning' : 'Sans planning'}</td>
                              <td className="p-2">{row.template_expected || '-'}</td>
                              <td className="p-2">{row.real_status || '-'}</td>
                              <td className="p-2">{row.reason || '-'}</td>
                              <td className="p-2">{formatDate(row.attempted_at)}</td>
                              <td className="p-2">{row.manual_review_required ? 'Oui' : 'Non'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card data-testid="advanced-history-card">
              <CardHeader className="pb-3 space-y-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <CardTitle className="text-lg" data-testid="advanced-history-title">Historique professionnel des e-mails</CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button data-testid="history-export-messages-button" variant="outline" size="sm" onClick={() => exportHistory('messages')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export synthèse
                    </Button>
                    <Button data-testid="history-export-attempts-button" variant="outline" size="sm" onClick={() => exportHistory('tentatives')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export tentatives
                    </Button>
                  </div>
                </div>

                <div className="space-y-2" data-testid="history-period-quick-actions">
                  <p className="text-xs text-slate-600">Périodes rapides</p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" data-testid="period-today" onClick={() => setQuickPeriod('today')}>Aujourd&apos;hui</Button>
                    <Button size="sm" variant="outline" data-testid="period-7d" onClick={() => setQuickPeriod('7d')}>7 derniers jours</Button>
                    <Button size="sm" variant="outline" data-testid="period-30d" onClick={() => setQuickPeriod('30d')}>30 derniers jours</Button>
                    <Button size="sm" variant="outline" data-testid="period-current-month" onClick={() => setQuickPeriod('current_month')}>Mois en cours</Button>
                    <Button size="sm" variant="outline" data-testid="period-previous-month" onClick={() => setQuickPeriod('previous_month')}>Mois précédent</Button>
                    <Button size="sm" variant="outline" data-testid="period-3m" onClick={() => setQuickPeriod('3m')}>3 derniers mois</Button>
                    <Button size="sm" variant="outline" data-testid="period-6m" onClick={() => setQuickPeriod('6m')}>6 derniers mois</Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="history-custom-period-grid">
                  <div>
                    <label className="text-xs text-slate-600">Date de début</label>
                    <Input data-testid="custom-period-start" type="date" value={historyPeriod.start_date} onChange={(e) => setHistoryPeriod(prev => ({ ...prev, preset: 'custom', start_date: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">Date de fin</label>
                    <Input data-testid="custom-period-end" type="date" value={historyPeriod.end_date} onChange={(e) => setHistoryPeriod(prev => ({ ...prev, preset: 'custom', end_date: e.target.value }))} />
                  </div>
                  <div className="flex items-end">
                    <Button data-testid="apply-custom-period" className="w-full" onClick={() => fetchEmailHistory({ page: 1, start_date: historyPeriod.start_date, end_date: historyPeriod.end_date })}>
                      Appliquer la période personnalisée
                    </Button>
                  </div>
                  <div className="flex items-end">
                    <Button data-testid="reset-history-filters" variant="outline" className="w-full" onClick={resetHistoryFilters}>
                      Réinitialiser les filtres
                    </Button>
                  </div>
                </div>

                <div className="text-xs text-slate-600" data-testid="history-active-filters-count">
                  Filtres actifs: <strong>{historyData.active_filters_count || 0}</strong>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="history-filters-grid">
                  <div className="relative lg:col-span-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input data-testid="filter-search" value={historyFilter.search} onChange={(e) => setHistoryFilter({ ...historyFilter, search: e.target.value })} placeholder="Recherche globale" className="pl-9" />
                  </div>
                  <Input data-testid="filter-last-name" value={historyFilter.last_name} onChange={(e) => setHistoryFilter({ ...historyFilter, last_name: e.target.value })} placeholder="Nom" />
                  <Input data-testid="filter-first-name" value={historyFilter.first_name} onChange={(e) => setHistoryFilter({ ...historyFilter, first_name: e.target.value })} placeholder="Prénom" />
                  <Input data-testid="filter-email" value={historyFilter.recipient_email} onChange={(e) => setHistoryFilter({ ...historyFilter, recipient_email: e.target.value })} placeholder="Adresse e-mail" />
                  <Input data-testid="filter-employee-name" value={historyFilter.employee_name} onChange={(e) => setHistoryFilter({ ...historyFilter, employee_name: e.target.value })} placeholder="Nom complet salarié" />

                  <Input list="sites-list" data-testid="filter-site" value={historyFilter.site_id} onChange={(e) => setHistoryFilter({ ...historyFilter, site_id: e.target.value })} placeholder="Site" />
                  <datalist id="sites-list">
                    {(historyData.filter_options?.sites || []).map((site) => (
                      <option key={site.value} value={site.value}>{site.label}</option>
                    ))}
                  </datalist>

                  <Input list="categories-list" data-testid="filter-category" value={historyFilter.category} onChange={(e) => setHistoryFilter({ ...historyFilter, category: e.target.value })} placeholder="Catégorie" />
                  <datalist id="categories-list">
                    {(historyData.filter_options?.categories || []).map((item) => (
                      <option key={item} value={item} />
                    ))}
                  </datalist>

                  <Input list="templates-list" data-testid="filter-template" value={historyFilter.template} onChange={(e) => setHistoryFilter({ ...historyFilter, template: e.target.value })} placeholder="Modèle" />
                  <datalist id="templates-list">
                    {(historyData.filter_options?.templates || []).map((item) => (
                      <option key={item} value={item} />
                    ))}
                  </datalist>

                  <select data-testid="filter-status" value={historyFilter.status} onChange={(e) => setHistoryFilter({ ...historyFilter, status: e.target.value })} className="h-10 rounded-md border px-3 text-sm">
                    <option value="all">Tous statuts</option>
                    {(historyData.filter_options?.statuses || []).map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>

                  <select data-testid="filter-dispatch-mode" value={historyFilter.dispatch_mode} onChange={(e) => setHistoryFilter({ ...historyFilter, dispatch_mode: e.target.value })} className="h-10 rounded-md border px-3 text-sm">
                    <option value="all">Tous modes</option>
                    {(historyData.filter_options?.dispatch_modes || []).map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>

                  <Input list="week-start-list" data-testid="filter-week-start" value={historyFilter.week_start} onChange={(e) => setHistoryFilter({ ...historyFilter, week_start: e.target.value })} placeholder="Semaine planning (YYYY-MM-DD)" />
                  <datalist id="week-start-list">
                    {(historyData.filter_options?.week_starts || []).map((item) => (
                      <option key={item} value={item} />
                    ))}
                  </datalist>

                  <Input list="trigger-list" data-testid="filter-trigger" value={historyFilter.trigger} onChange={(e) => setHistoryFilter({ ...historyFilter, trigger: e.target.value })} placeholder="Déclencheur" />
                  <datalist id="trigger-list">
                    {(historyData.filter_options?.triggers || []).map((item) => (
                      <option key={item} value={item} />
                    ))}
                  </datalist>

                  <Input list="manual-admin-list" data-testid="filter-manual-admin" value={historyFilter.manual_admin} onChange={(e) => setHistoryFilter({ ...historyFilter, manual_admin: e.target.value })} placeholder="Administrateur (manuel)" />
                  <datalist id="manual-admin-list">
                    {(historyData.filter_options?.manual_admins || []).map((item) => (
                      <option key={item} value={item} />
                    ))}
                  </datalist>

                  <select data-testid="filter-has-error" value={historyFilter.has_error} onChange={(e) => setHistoryFilter({ ...historyFilter, has_error: e.target.value })} className="h-10 rounded-md border px-3 text-sm">
                    <option value="all">Erreur: Tous</option>
                    <option value="true">Avec erreur</option>
                    <option value="false">Sans erreur</option>
                  </select>
                </div>

                <div className="flex justify-end gap-2">
                  <Button data-testid="apply-history-filters-button" onClick={applyHistoryFilters}>
                    <Filter className="w-4 h-4 mr-2" />
                    Appliquer les filtres
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 text-xs" data-testid="history-summary-cards-grid">
                  <div className="p-3 rounded-lg bg-blue-50">Messages uniques: <strong data-testid="kpi-unique-messages">{historyData.counters?.messages_uniques ?? 0}</strong></div>
                  <div className="p-3 rounded-lg bg-slate-50">Destinataires uniques: <strong data-testid="kpi-unique-recipients">{historyData.counters?.destinataires_uniques ?? 0}</strong></div>
                  <div className="p-3 rounded-lg bg-emerald-50">Envoyés: <strong data-testid="kpi-sent">{historyData.counters?.envoyes ?? 0}</strong></div>
                  <div className="p-3 rounded-lg bg-emerald-100">Livrés: <strong data-testid="kpi-delivered">{historyData.counters?.livres ?? 0}</strong></div>
                  <div className="p-3 rounded-lg bg-yellow-50">En attente: <strong data-testid="kpi-pending">{historyData.counters?.en_attente ?? 0}</strong></div>
                  <div className="p-3 rounded-lg bg-red-50">Échecs: <strong data-testid="kpi-failed">{historyData.counters?.echecs ?? 0}</strong></div>
                  <div className="p-3 rounded-lg bg-orange-50">Rejetés: <strong data-testid="kpi-rejected">{historyData.counters?.rejetes ?? 0}</strong></div>
                  <div className="p-3 rounded-lg bg-indigo-50">Simulations: <strong data-testid="kpi-simulated">{historyData.counters?.simules ?? 0}</strong></div>
                  <div className="p-3 rounded-lg bg-zinc-50">Ignorés/Annulés: <strong data-testid="kpi-ignored-cancelled">{(historyData.counters?.ignores ?? 0) + (historyData.counters?.annules ?? 0)}</strong></div>
                  <div className="p-3 rounded-lg bg-purple-50">Tentatives: <strong data-testid="kpi-attempts">{historyData.counters?.tentatives_totales ?? 0}</strong></div>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                <div className="text-xs text-slate-600" data-testid="history-result-range">
                  Résultats {historyData.pagination?.result_start ?? 0} à {historyData.pagination?.result_end ?? 0} sur {historyData.pagination?.total_results ?? 0}
                </div>

                {historyLoading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : emailHistory.length === 0 ? (
                  <div className="p-8 text-center text-gray-500" data-testid="history-empty-state">
                    <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Aucun e-mail trouvé pour la période et les filtres sélectionnés.</p>
                  </div>
                ) : (
                  <div className="overflow-auto rounded-lg border" data-testid="history-main-table-wrapper">
                    <table className="w-full min-w-[1200px] text-xs">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-2">Date/Heure (Europe/Paris)</th>
                          <th className="text-left p-2">Nom et prénom</th>
                          <th className="text-left p-2">Adresse e-mail</th>
                          <th className="text-left p-2">Objet</th>
                          <th className="text-left p-2">Catégorie</th>
                          <th className="text-left p-2">Modèle</th>
                          <th className="text-left p-2">Déclenchement</th>
                          <th className="text-left p-2">Statut</th>
                          <th className="text-left p-2">Erreur courte</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailHistory.map((row, idx) => {
                          const statusConfig = STATUS_CONFIG[row.status_group] || STATUS_CONFIG.unknown;
                          return (
                            <tr key={row.message_key || row.email_id || idx} className="border-b last:border-b-0 hover:bg-slate-50 cursor-pointer" onClick={() => fetchEmailDetail(row.email_id)} data-testid={`history-row-${idx}`}>
                              <td className="p-2 whitespace-nowrap">{row.display_created_at_paris || '-'}</td>
                              <td className="p-2">{row.recipient_name || '-'}</td>
                              <td className="p-2">{row.recipient_email || '-'}</td>
                              <td className="p-2 max-w-[280px] truncate" title={row.subject || ''}>{row.subject || '-'}</td>
                              <td className="p-2">{row.category || '-'}</td>
                              <td className="p-2">{row.template || '-'}</td>
                              <td className="p-2">{row.dispatch_mode === 'automatique' ? 'Automatique' : 'Manuel'}</td>
                              <td className="p-2">
                                <span className={`px-2 py-1 rounded-full ${statusConfig.color}`}>{row.status_label || 'Statut inconnu'}</span>
                              </td>
                              <td className="p-2 text-red-700 max-w-[240px] truncate" title={row.short_error || ''}>{row.short_error || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid="history-pagination-controls">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600">Lignes par page</span>
                    <select
                      data-testid="history-page-size-select"
                      value={historyPagination.page_size}
                      onChange={(e) => fetchEmailHistory({ page: 1, page_size: Number(e.target.value) })}
                      className="h-9 rounded-md border px-2 text-sm"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      data-testid="history-prev-page"
                      variant="outline"
                      size="sm"
                      disabled={!historyData.pagination?.has_prev}
                      onClick={() => fetchEmailHistory({ page: (historyData.pagination?.page || 1) - 1 })}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Précédent
                    </Button>
                    <span className="text-xs text-slate-600" data-testid="history-page-indicator">Page {historyData.pagination?.page || 1} / {historyData.pagination?.total_pages || 1}</span>
                    <Button
                      data-testid="history-next-page"
                      variant="outline"
                      size="sm"
                      disabled={!historyData.pagination?.has_next}
                      onClick={() => fetchEmailHistory({ page: (historyData.pagination?.page || 1) + 1 })}
                    >
                      Suivant
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="md:col-span-2" data-testid="email-automation-card">
              <CardHeader>
                <CardTitle className="text-lg">Automatisations e-mails — {environmentLabel}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 rounded-lg border bg-blue-50 text-blue-900 text-sm" data-testid="email-automation-mode-banner">
                  Automatisations actives — déclenchements manuels désactivés.
                </div>
                {!realSendEnabled && (
                  <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm" data-testid="email-real-send-disabled-banner">
                    Envois réels désactivés côté backend. Les actions restent en simulation uniquement.
                  </div>
                )}

                <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm" data-testid="manual-mass-send-disabled-banner">
                  Politique active: tous les déclenchements manuels (unitaires et en masse) sont désactivés.
                </div>

                <div className="rounded-xl border p-4 bg-slate-50 space-y-3" data-testid="readonly-recipients-refresh-panel">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900" data-testid="readonly-recipients-title">Prévisualisation sécurisée des destinataires</p>
                      <p className="text-xs text-slate-700" data-testid="readonly-recipients-message">Calcul en lecture seule — aucun e-mail envoyé</p>
                    </div>
                    <Button
                      data-testid="refresh-readonly-recipients-button"
                      variant="outline"
                      onClick={refreshReadonlyRecipients}
                      disabled={readonlyRecipientsLoading}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${readonlyRecipientsLoading ? 'animate-spin' : ''}`} />
                      {readonlyRecipientsLoading ? 'Calcul en cours...' : 'Actualiser les destinataires'}
                    </Button>
                  </div>

                  {readonlyRecipientsError && (
                    <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-xs" data-testid="readonly-recipients-error">
                      {readonlyRecipientsError}
                    </div>
                  )}

                  {readonlyRecipientsResult && (
                    <div className="space-y-4" data-testid="readonly-recipients-results">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        <div className="p-3 rounded-lg border bg-white" data-testid="readonly-welcome-summary">
                          <p className="font-semibold text-gray-800">Bienvenue</p>
                          <p>Éligibles: <strong data-testid="readonly-welcome-eligible-count">{readonlyRecipientsResult.welcome?.eligible_count ?? 0}</strong></p>
                          <p>Exclus: <strong data-testid="readonly-welcome-excluded-count">{readonlyRecipientsResult.welcome?.excluded_count ?? 0}</strong></p>
                          <p>Rôles ciblés (éligibles): <strong data-testid="readonly-welcome-role-scope">employee uniquement</strong></p>
                        </div>
                        <div className="p-3 rounded-lg border bg-white" data-testid="readonly-weekly-summary">
                          <p className="font-semibold text-gray-800">Rappel hebdomadaire</p>
                          <p>Éligibles: <strong data-testid="readonly-weekly-eligible-count">{readonlyRecipientsResult.weekly_reminder?.eligible_count ?? 0}</strong></p>
                          <p>Exclus: <strong data-testid="readonly-weekly-excluded-count">{readonlyRecipientsResult.weekly_reminder?.excluded_count ?? 0}</strong></p>
                          <p>Rôles ciblés (éligibles): <strong data-testid="readonly-weekly-role-scope">employee uniquement</strong></p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <div className="rounded-lg border bg-white p-3 space-y-3" data-testid="readonly-welcome-details-panel">
                          <p className="text-sm font-semibold text-gray-800">Bienvenue — Détails destinataires</p>

                          <div className="rounded border p-2" data-testid="readonly-welcome-eligible-table">
                            <p className="text-xs font-medium text-gray-700 mb-2">Éligibles (lecture seule)</p>
                            <div className="max-h-40 overflow-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="text-left p-2">user_id</th>
                                    <th className="text-left p-2">Rôle</th>
                                    <th className="text-left p-2">E-mail masqué</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(readonlyRecipientsResult.welcome?.eligible_preview || []).map((row, idx) => (
                                    <tr key={`welcome-eligible-${row.user_id || idx}`} className="border-t">
                                      <td className="p-2">{row.user_id || '-'}</td>
                                      <td className="p-2" data-testid={`readonly-welcome-eligible-role-${idx}`}>{row.role || '-'}</td>
                                      <td className="p-2 break-all">{row.masked_email || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="rounded border p-2" data-testid="readonly-welcome-excluded-table">
                            <p className="text-xs font-medium text-gray-700 mb-2">Exclusions (raisons)</p>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {Object.entries(readonlyRecipientsResult.welcome?.excluded_by_reason || {}).map(([reason, count]) => (
                                <span key={`welcome-reason-${reason}`} className="px-2 py-1 rounded bg-amber-100 text-amber-900 text-xs" data-testid={`readonly-welcome-exclusion-reason-${reason}`}>
                                  {reason}: {count}
                                </span>
                              ))}
                            </div>
                            <div className="max-h-44 overflow-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="text-left p-2">user_id</th>
                                    <th className="text-left p-2">Rôle</th>
                                    <th className="text-left p-2">E-mail masqué</th>
                                    <th className="text-left p-2">Raison</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(readonlyRecipientsResult.welcome?.excluded_preview || []).map((row, idx) => (
                                    <tr key={`welcome-excluded-${row.user_id || idx}`} className="border-t">
                                      <td className="p-2">{row.user_id || '-'}</td>
                                      <td className="p-2" data-testid={`readonly-welcome-excluded-role-${idx}`}>{row.role || '-'}</td>
                                      <td className="p-2 break-all">{row.masked_email || '-'}</td>
                                      <td className="p-2" data-testid={`readonly-welcome-excluded-reason-${idx}`}>{row.reason || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border bg-white p-3 space-y-3" data-testid="readonly-weekly-details-panel">
                          <p className="text-sm font-semibold text-gray-800">Rappel hebdomadaire — Détails destinataires</p>

                          <div className="rounded border p-2" data-testid="readonly-weekly-eligible-table">
                            <p className="text-xs font-medium text-gray-700 mb-2">Éligibles (lecture seule)</p>
                            <div className="max-h-40 overflow-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="text-left p-2">user_id</th>
                                    <th className="text-left p-2">Rôle</th>
                                    <th className="text-left p-2">E-mail masqué</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(readonlyRecipientsResult.weekly_reminder?.eligible_preview || []).map((row, idx) => (
                                    <tr key={`weekly-eligible-${row.user_id || idx}`} className="border-t">
                                      <td className="p-2">{row.user_id || '-'}</td>
                                      <td className="p-2" data-testid={`readonly-weekly-eligible-role-${idx}`}>{row.role || '-'}</td>
                                      <td className="p-2 break-all">{row.masked_email || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="rounded border p-2" data-testid="readonly-weekly-excluded-table">
                            <p className="text-xs font-medium text-gray-700 mb-2">Exclusions (raisons)</p>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {Object.entries(readonlyRecipientsResult.weekly_reminder?.excluded_by_reason || {}).map(([reason, count]) => (
                                <span key={`weekly-reason-${reason}`} className="px-2 py-1 rounded bg-amber-100 text-amber-900 text-xs" data-testid={`readonly-weekly-exclusion-reason-${reason}`}>
                                  {reason}: {count}
                                </span>
                              ))}
                            </div>
                            <div className="max-h-44 overflow-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="text-left p-2">user_id</th>
                                    <th className="text-left p-2">Rôle</th>
                                    <th className="text-left p-2">E-mail masqué</th>
                                    <th className="text-left p-2">Raison</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(readonlyRecipientsResult.weekly_reminder?.excluded_preview || []).map((row, idx) => (
                                    <tr key={`weekly-excluded-${row.user_id || idx}`} className="border-t">
                                      <td className="p-2">{row.user_id || '-'}</td>
                                      <td className="p-2" data-testid={`readonly-weekly-excluded-role-${idx}`}>{row.role || '-'}</td>
                                      <td className="p-2 break-all">{row.masked_email || '-'}</td>
                                      <td className="p-2" data-testid={`readonly-weekly-excluded-reason-${idx}`}>{row.reason || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border p-4 bg-emerald-50 space-y-3" data-testid="planning-email-audit-readonly-panel">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-emerald-900" data-testid="planning-audit-title">
                        Diagnostic planning hebdomadaire (lecture seule)
                      </p>
                      <p className="text-xs text-emerald-800" data-testid="planning-audit-message">
                        Aucune relance, aucun envoi, aucune modification de données.
                      </p>
                    </div>
                    <Button
                      data-testid="run-planning-audit-readonly-button"
                      variant="outline"
                      onClick={runPlanningEmailReadonlyAudit}
                      disabled={planningAuditLoading}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${planningAuditLoading ? 'animate-spin' : ''}`} />
                      {planningAuditLoading ? 'Diagnostic en cours...' : 'Lancer diagnostic lecture seule'}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="planning-audit-inputs-grid">
                    <div>
                      <label className="text-xs text-slate-600">Semaine auditée (lundi)</label>
                      <Input
                        data-testid="planning-audit-week-start-input"
                        type="date"
                        value={planningAuditForm.week_start}
                        onChange={(e) => setPlanningAuditForm(prev => ({ ...prev, week_start: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600">Total prévu déclaré</label>
                      <Input
                        data-testid="planning-audit-reported-expected-input"
                        type="number"
                        value={planningAuditForm.reported_expected_count}
                        onChange={(e) => setPlanningAuditForm(prev => ({ ...prev, reported_expected_count: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600">Total reçu déclaré</label>
                      <Input
                        data-testid="planning-audit-reported-received-input"
                        type="number"
                        value={planningAuditForm.reported_received_count}
                        onChange={(e) => setPlanningAuditForm(prev => ({ ...prev, reported_received_count: e.target.value }))}
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="text-xs flex items-center gap-2 text-slate-700" data-testid="planning-audit-include-manual-toggle">
                        <input
                          type="checkbox"
                          checked={planningAuditForm.include_manual_trigger}
                          onChange={(e) => setPlanningAuditForm(prev => ({ ...prev, include_manual_trigger: e.target.checked }))}
                        />
                        Inclure trigger manuel de publication
                      </label>
                    </div>
                  </div>

                  {planningAuditError && (
                    <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-xs" data-testid="planning-audit-error">
                      {planningAuditError}
                    </div>
                  )}

                  {planningAuditResult && (
                    <div className="space-y-3" data-testid="planning-audit-results">
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 text-xs">
                        <div className="p-2 rounded border bg-white" data-testid="planning-audit-count-expected">Éligibles: <strong>{planningAuditResult.counts?.eligible_total ?? planningAuditResult.counts?.expected_valid_total ?? 0}</strong></div>
                        <div className="p-2 rounded border bg-white" data-testid="planning-audit-count-with-schedule">Avec planning: <strong>{planningAuditResult.counts?.eligible_with_schedule_total ?? 0}</strong></div>
                        <div className="p-2 rounded border bg-white" data-testid="planning-audit-count-without-schedule">Sans planning: <strong>{planningAuditResult.counts?.eligible_without_schedule_total ?? 0}</strong></div>
                        <div className="p-2 rounded border bg-white" data-testid="planning-audit-count-sent">Envoyés: <strong>{planningAuditResult.counts?.actual_sent_total ?? 0}</strong></div>
                        <div className="p-2 rounded border bg-white" data-testid="planning-audit-count-failed">Échecs: <strong>{planningAuditResult.counts?.actual_failed_total ?? 0}</strong></div>
                        <div className="p-2 rounded border bg-white" data-testid="planning-audit-count-missing">Manquants: <strong>{planningAuditResult.counts?.missing_from_expected_total ?? 0}</strong></div>
                      </div>

                      <div className="text-xs text-slate-700 p-2 rounded border bg-white" data-testid="planning-audit-trigger-mode">
                        Mode détecté: <strong>{planningAuditResult.audit_window?.detected_trigger_mode || '-'}</strong>
                        {' • '}Semaine: <strong>{planningAuditResult.audit_window?.week_start} → {planningAuditResult.audit_window?.week_end}</strong>
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        <div className="rounded border bg-white p-2" data-testid="planning-audit-missing-table">
                          <p className="text-xs font-semibold text-slate-700 mb-2">Salariés manquants (rattrapage ciblé)</p>
                          <div className="max-h-44 overflow-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-50">
                                <tr>
                                  <th className="text-left p-2">user_id</th>
                                  <th className="text-left p-2">E-mail masqué</th>
                                  <th className="text-left p-2">Raison</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(planningAuditResult.missing_recipients || []).map((row, idx) => (
                                  <tr key={`planning-missing-${row.user_id || idx}`} className="border-t">
                                    <td className="p-2">{row.user_id || '-'}</td>
                                    <td className="p-2">{row.masked_email || '-'}</td>
                                    <td className="p-2">{row.reason || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="rounded border bg-white p-2" data-testid="planning-audit-failed-table">
                          <p className="text-xs font-semibold text-slate-700 mb-2">Échecs / rejets détectés</p>
                          <div className="max-h-44 overflow-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-50">
                                <tr>
                                  <th className="text-left p-2">user_id</th>
                                  <th className="text-left p-2">E-mail masqué</th>
                                  <th className="text-left p-2">Erreur</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(planningAuditResult.failed_deliveries || []).map((row, idx) => (
                                  <tr key={`planning-failed-${row.user_id || idx}`} className="border-t">
                                    <td className="p-2">{row.user_id || '-'}</td>
                                    <td className="p-2">{row.masked_email || '-'}</td>
                                    <td className="p-2">{row.error || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      <div className="p-2 rounded border bg-white text-xs" data-testid="planning-audit-catchup-simulation">
                        Simulation de rattrapage (lecture seule): <strong>{planningAuditResult.catchup_simulation?.would_send_count ?? 0}</strong> salarié(s) ciblé(s) uniquement manquants.
                      </div>
                    </div>
                  )}
                </div>

                {automationStatusSummary && (
                  <div className="rounded-xl border p-4 bg-white space-y-3" data-testid="automation-readonly-status-summary">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-800">État des automatisations</p>
                      <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700" data-testid="automation-timezone-pill">
                        Fuseau: {automationStatusSummary.timezone || 'Europe/Paris'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg border" data-testid="welcome-readonly-status-card">
                        <p className="text-sm font-medium text-gray-800">E-mail de bienvenue</p>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          <div>Statut: <strong data-testid="welcome-status-text">{getAutomationStateLabel(automationStatusSummary.welcome?.active)}</strong></div>
                          <div>Prochaine vérification: <strong data-testid="welcome-next-run">{formatDate(automationStatusSummary.welcome?.next_check_at || automationStatusSummary.welcome?.next_scheduled_at)}</strong></div>
                          <div>Dernier envoi: <strong data-testid="welcome-last-run">{formatDate(automationStatusSummary.welcome?.last_sent_at)}</strong></div>
                          <div>Destinataires: <strong data-testid="welcome-last-recipients">{automationStatusSummary.welcome?.last_recipients ?? 0}</strong></div>
                          <div>Réussites: <strong data-testid="welcome-last-success">{automationStatusSummary.welcome?.last_success ?? 0}</strong></div>
                          <div>Échecs: <strong data-testid="welcome-last-failed">{automationStatusSummary.welcome?.last_failed ?? 0}</strong></div>
                          <div>Doublons bloqués: <strong data-testid="welcome-last-duplicates">{automationStatusSummary.welcome?.last_duplicates_blocked ?? 0}</strong></div>
                        </div>
                      </div>

                      <div className="p-3 rounded-lg border" data-testid="weekly-readonly-status-card">
                        <p className="text-sm font-medium text-gray-800">Rappel hebdomadaire</p>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          <div>Statut: <strong data-testid="weekly-status-text">{getAutomationStateLabel(automationStatusSummary.weekly_reminder?.active)}</strong></div>
                          <div>Prochain envoi: <strong data-testid="weekly-next-run">{formatDate(automationStatusSummary.weekly_reminder?.next_scheduled_at)}</strong></div>
                          <div>Destinataires prévus: <strong data-testid="weekly-expected-recipients">{automationStatusSummary.weekly_reminder?.current_expected_recipients ?? 0} salariés actifs</strong></div>
                          <div>Calculé à (pré-envoi): <strong data-testid="weekly-precompute-at">{formatDate(automationStatusSummary.weekly_reminder?.precompute_calculated_at)}</strong></div>
                          <div>Dernier envoi: <strong data-testid="weekly-last-run">{formatDate(automationStatusSummary.weekly_reminder?.last_sent_at)}</strong></div>
                          <div>Destinataires prévus (dernier envoi): <strong data-testid="weekly-last-planned-recipients">{automationStatusSummary.weekly_reminder?.last_planned_recipients ?? 0}</strong></div>
                          <div>E-mails envoyés: <strong data-testid="weekly-last-recipients">{automationStatusSummary.weekly_reminder?.last_recipients ?? 0}</strong></div>
                          <div>Réussites: <strong data-testid="weekly-last-success">{automationStatusSummary.weekly_reminder?.last_success ?? 0}</strong></div>
                          <div>Échecs: <strong data-testid="weekly-last-failed">{automationStatusSummary.weekly_reminder?.last_failed ?? 0}</strong></div>
                          <div>Sans adresse valide: <strong data-testid="weekly-last-invalid-email">{automationStatusSummary.weekly_reminder?.last_invalid_email ?? automationStatusSummary.weekly_reminder?.current_invalid_email ?? 0}</strong></div>
                          <div>Doublons bloqués: <strong data-testid="weekly-last-duplicates">{automationStatusSummary.weekly_reminder?.last_duplicates_blocked ?? 0}</strong></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl border space-y-3" data-testid="welcome-automation-panel">
                    <h3 className="font-semibold text-gray-800">E-mail de bienvenue</h3>
                    <p className="text-xs text-gray-600">
                      Cible: salariés actifs + approuvés + site affecté, anti-doublon strict.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        data-testid="run-welcome-test-button"
                        onClick={() => runAutomation('welcome', false)}
                        disabled
                        variant="outline"
                      >
                        Déclenchement manuel désactivé
                      </Button>
                      <Button
                        data-testid="toggle-welcome-real-send-button"
                        onClick={() => setRealSendMode(prev => ({ ...prev, welcome: !prev.welcome }))}
                        variant="ghost"
                        disabled
                      >
                        {realSendMode.welcome ? 'Masquer informations contrôlées' : 'Afficher informations contrôlées'}
                      </Button>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border space-y-3" data-testid="reminder-automation-panel">
                    <h3 className="font-semibold text-gray-800">Rappel hebdomadaire lundi 07:00</h3>
                    <p className="text-xs text-gray-600">
                      Cible: tous les salariés actifs et approuvés (`role=employee`).
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        data-testid="run-weekly-reminder-test-button"
                        onClick={() => runAutomation('reminder', false)}
                        disabled
                        variant="outline"
                      >
                        Déclenchement manuel désactivé
                      </Button>
                      <Button
                        data-testid="toggle-reminder-real-send-button"
                        onClick={() => setRealSendMode(prev => ({ ...prev, reminder: !prev.reminder }))}
                        variant="ghost"
                        disabled
                      >
                        {realSendMode.reminder ? 'Masquer informations contrôlées' : 'Afficher informations contrôlées'}
                      </Button>
                    </div>
                  </div>
                </div>

                {(realSendMode.welcome || realSendMode.reminder) && (
                  <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 space-y-3" data-testid="controlled-real-send-panel">
                    <p className="text-sm font-medium text-amber-900">
                      Informations contrôlées (lecture seule)
                    </p>
                    <Input
                      data-testid="controlled-real-send-email-input"
                      placeholder="email.test@votredomaine.com"
                      value={realSendEmail}
                      onChange={(e) => setRealSendEmail(e.target.value)}
                    />
                    <div className="flex flex-wrap gap-2">
                      {realSendMode.welcome && (
                        <Button
                          data-testid="run-welcome-real-send-button"
                          onClick={() => runAutomation('welcome', true)}
                          disabled
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          Action désactivée
                        </Button>
                      )}
                      {realSendMode.reminder && (
                        <Button
                          data-testid="run-reminder-real-send-button"
                          onClick={() => runAutomation('reminder', true)}
                          disabled
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          Action désactivée
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {!isProductionEnvironment && (
                <div className="p-4 rounded-xl border border-indigo-200 bg-indigo-50 space-y-3" data-testid="validation-compare-panel">
                  <p className="text-sm font-medium text-indigo-900" data-testid="validation-compare-title">
                    Mode validation avant prod (simulation Preview vs envoi réel contrôlé)
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <select
                      data-testid="validation-type-select"
                      className="h-10 rounded-md border px-3 text-sm"
                      value={validationMode.type}
                      onChange={(e) => setValidationMode(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option value="both">Bienvenue + Rappel</option>
                      <option value="welcome">Bienvenue uniquement</option>
                      <option value="weekly">Rappel uniquement</option>
                    </select>
                    <Input
                      data-testid="validation-user-id-input"
                      placeholder="user_id optionnel (ciblage 1 salarié)"
                      value={validationMode.userId}
                      onChange={(e) => setValidationMode(prev => ({ ...prev, userId: e.target.value.trim() }))}
                    />
                    <Button
                      data-testid="run-validation-compare-button"
                      onClick={runValidationCompare}
                      disabled
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      Comparaison manuelle désactivée
                    </Button>
                  </div>

                  {validationResult && (
                    <div className="rounded-lg border bg-white p-3 space-y-2" data-testid="validation-compare-result">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-gray-800">Résultat validation</span>
                        <span
                          data-testid="validation-compare-badge"
                          className={`px-2 py-1 text-xs rounded ${validationResult.mismatch_detected ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}
                        >
                          {validationResult.mismatch_detected ? 'Différences détectées' : 'Validation OK'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600" data-testid="validation-compare-message">{validationResult.message}</p>
                      {validationResult.real_probe_enabled === false && (
                        <p className="text-xs text-amber-700" data-testid="validation-real-probe-disabled-note">
                          Mode simulation contrôlée: aucun e-mail réel n&apos;a été envoyé.
                        </p>
                      )}
                      {Array.isArray(validationResult.mismatch_details) && validationResult.mismatch_details.length > 0 && (
                        <ul className="text-xs list-disc pl-5 text-red-700" data-testid="validation-mismatch-list">
                          {validationResult.mismatch_details.map((item, idx) => (
                            <li key={`${item}-${idx}`}>{item}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
                )}

                {isProductionEnvironment && (
                  <div className="p-4 rounded-xl border border-indigo-200 bg-indigo-50 space-y-2" data-testid="validation-readonly-production-panel">
                    <p className="text-sm font-medium text-indigo-900">Mode validation avant prod</p>
                    <p className="text-xs text-indigo-800">
                      Bloc affiché en lecture seule. Les comparaisons manuelles sont désactivées en production.
                    </p>
                  </div>
                )}

                {automationResult && (
                  <div className="p-4 rounded-xl border bg-gray-50" data-testid="automation-last-run-summary">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="font-semibold text-gray-800">
                        Dernière exécution: {automationResult.type === 'welcome' ? 'Bienvenue' : 'Rappel lundi'}
                      </p>
                      <span className={`px-2 py-1 rounded text-xs ${automationResult.test_mode ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                        {automationResult.test_mode ? 'Mode test' : 'Envoi réel contrôlé'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div data-testid="automation-stat-eligible">Éligibles: <strong>{automationResult.eligible_count ?? 0}</strong></div>
                      <div data-testid="automation-stat-processed">Traités: <strong>{automationResult.processed ?? 0}</strong></div>
                      <div data-testid="automation-stat-sent-simulated">Envoyés/Simulés: <strong>{(automationResult.sent ?? 0) + (automationResult.simulated ?? 0)}</strong></div>
                      <div data-testid="automation-stat-failed">Échecs: <strong>{automationResult.failed ?? 0}</strong></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs mt-2">
                      <div data-testid="automation-stat-duplicates">Doublons évités: <strong>{automationResult.duplicates_skipped ?? 0}</strong></div>
                      <div data-testid="automation-stat-missing-email">Sans email: <strong>{automationResult.missing_email_skipped ?? 0}</strong></div>
                      <div data-testid="automation-stat-roles">Rôles ciblés: <strong>{[...new Set((automationResult.details || []).map(d => d.role).filter(Boolean))].join(', ') || '-'}</strong></div>
                      {automationResult.week_key && (
                        <div data-testid="automation-stat-week-key">Semaine: <strong>{automationResult.week_key}</strong></div>
                      )}
                    </div>
                    <div className="mt-3 max-h-44 overflow-auto rounded border bg-white" data-testid="automation-details-list">
                      {(automationResult.details || []).slice(0, 50).map((entry, idx) => (
                        <div key={`${entry.user_id}-${idx}`} className="text-xs px-3 py-2 border-b last:border-b-0">
                          <span className="font-medium">{entry.employee_name || entry.user_id}</span>
                          <span className="text-gray-500"> — {entry.status} ({entry.role || 'n/a'})</span>
                          <div className="text-gray-500 break-all">
                            {entry.original_recipient} → {entry.final_recipient}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Email Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {emailConfig ? (
                  <>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Service Resend</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        emailConfig.configured 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {emailConfig.configured ? 'Actif' : 'Mode simulation'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Email expéditeur</span>
                      <span className="text-sm font-medium">{emailConfig.sender_email}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Nom expéditeur</span>
                      <span className="text-sm font-medium">{emailConfig.sender_name}</span>
                    </div>
                  </>
                ) : (
                  <div className="animate-pulse space-y-3">
                    <div className="h-10 bg-gray-200 rounded"></div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Statistics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Statistiques</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {emailHistory.filter(e => e.status === 'sent').length}
                    </div>
                    <div className="text-xs text-green-700">Emails envoyés</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">
                      {emailHistory.filter(e => e.status === 'scheduled').length}
                    </div>
                    <div className="text-xs text-yellow-700">Programmés</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {emailHistory.filter(e => e.email_type === 'automatic').length}
                    </div>
                    <div className="text-xs text-blue-700">Automatiques</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {emailHistory.filter(e => e.status === 'failed').length}
                    </div>
                    <div className="text-xs text-red-700">Échecs</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Template count by category */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Modèles par catégorie</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(TEMPLATE_CATEGORIES).map(([key, cat]) => {
                    const count = templates.filter(t => getTemplateCategory(t.template_key) === key).length;
                    return (
                      <div key={key} className={`px-4 py-2 rounded-lg ${cat.color}`}>
                        <span className="mr-2">{cat.icon}</span>
                        <span className="font-medium">{cat.label}</span>
                        <span className="ml-2 opacity-70">({count})</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'incidents' && (
          <div className="space-y-6" data-testid="email-incidents-page">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Procédure de réactivation contrôlée (3 étapes)</CardTitle>
              </CardHeader>
              <CardContent>
                {reactivationProcedure ? (
                  <div className="space-y-3" data-testid="reactivation-procedure-card">
                    {(reactivationProcedure.steps || []).map((step) => (
                      <div key={step.step} className="p-3 rounded-lg border bg-gray-50">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-sm">Étape {step.step} — {step.name}</p>
                          <span className={`px-2 py-1 text-xs rounded ${step.status === 'available' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {step.status === 'available' ? 'Disponible' : 'Bloquée'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{step.description}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Chargement...</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tableau des incidents e-mails</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="email-incidents-filters">
                  <select
                    data-testid="incident-type-filter"
                    className="h-10 rounded-md border px-3 text-sm"
                    value={incidentFilter.incidentType}
                    onChange={(e) => setIncidentFilter(prev => ({ ...prev, incidentType: e.target.value }))}
                  >
                    <option value="all">Tous types d&apos;incident</option>
                    <option value="blocked_real_send">Envoi réel bloqué</option>
                    <option value="blocked_non_employee_target">Cible non employee bloquée</option>
                    <option value="blocked_invalid_email">Email invalide bloqué</option>
                    <option value="duplicate_prevented">Doublon empêché</option>
                  </select>

                  <select
                    data-testid="incident-email-type-filter"
                    className="h-10 rounded-md border px-3 text-sm"
                    value={incidentFilter.emailType}
                    onChange={(e) => setIncidentFilter(prev => ({ ...prev, emailType: e.target.value }))}
                  >
                    <option value="all">Tous emails</option>
                    <option value="welcome_employee_onboarding">Bienvenue</option>
                    <option value="weekly_mission_reminder">Rappel hebdo</option>
                  </select>

                  <Button
                    data-testid="refresh-incidents-button"
                    variant="outline"
                    onClick={fetchIncidents}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                  </Button>
                </div>

                {incidentsData.summary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="incidents-summary-stats">
                    <div className="p-2 rounded bg-red-50">Total: <strong>{incidentsData.summary.total || 0}</strong></div>
                    <div className="p-2 rounded bg-orange-50">Origines: <strong>{Object.keys(incidentsData.summary.by_origin || {}).length}</strong></div>
                    <div className="p-2 rounded bg-yellow-50">Rôles: <strong>{Object.keys(incidentsData.summary.by_role || {}).join(', ') || '-'}</strong></div>
                    <div className="p-2 rounded bg-blue-50">Types e-mail: <strong>{Object.keys(incidentsData.summary.by_email_type || {}).length}</strong></div>
                  </div>
                )}

                <div className="rounded-lg border overflow-hidden" data-testid="email-incidents-table">
                  {incidentsLoading ? (
                    <div className="p-4 text-sm text-gray-500">Chargement des incidents...</div>
                  ) : incidentsData.records.length === 0 ? (
                    <div className="p-4 text-sm text-gray-500">Aucun incident enregistré.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-2">Date</th>
                          <th className="text-left p-2">Type</th>
                          <th className="text-left p-2">Email</th>
                          <th className="text-left p-2">Rôle</th>
                          <th className="text-left p-2">Origine</th>
                          <th className="text-left p-2">Raison</th>
                        </tr>
                      </thead>
                      <tbody>
                        {incidentsData.records.map((row) => (
                          <tr key={row.incident_id} className="border-b last:border-b-0">
                            <td className="p-2 whitespace-nowrap">{formatDate(row.created_at)}</td>
                            <td className="p-2">{row.incident_type}</td>
                            <td className="p-2">{row.email_type}</td>
                            <td className="p-2">{row.role}</td>
                            <td className="p-2">{row.trigger_origin}</td>
                            <td className="p-2">{row.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-xs" data-testid="incidents-readonly-note">
                  Tableau en lecture seule: aucune action de relance n&apos;est disponible ici, pour éviter tout contournement des protections.
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Modal détail email */}
        {selectedEmail && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedEmail(null)}>
            <div 
              className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="email-detail-modal"
            >
              <div className="border-b p-4 flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <div>
                  <h2 className="text-xl font-bold">Détail de l&apos;email</h2>
                  <p className="text-blue-100 text-sm">
                    {formatDate(selectedEmail?.dates?.last_attempt_at || selectedEmail?.dates?.created_at)}
                  </p>
                </div>
                <button
                  data-testid="close-email-detail-modal-button"
                  onClick={() => setSelectedEmail(null)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingEmailDetail ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs" data-testid="email-detail-main-grid">
                      <div className="rounded-lg border p-3 bg-slate-50">
                        <p className="font-semibold mb-2">Destinataire</p>
                        <p><strong>Nom:</strong> {selectedEmail?.recipient?.name || '-'}</p>
                        <p><strong>E-mail:</strong> {selectedEmail?.recipient?.email || '-'}</p>
                        <p><strong>Site:</strong> {selectedEmail?.recipient?.site_name || '-'} ({selectedEmail?.recipient?.site_id || '-'})</p>
                      </div>

                      <div className="rounded-lg border p-3 bg-slate-50">
                        <p className="font-semibold mb-2">Statut</p>
                        <p><strong>Lisible:</strong> {selectedEmail?.status?.status_label || '-'}</p>
                        <p><strong>Brut:</strong> {selectedEmail?.status?.status_raw || '-'}</p>
                        <p><strong>Prestataire:</strong> {selectedEmail?.status?.provider_status || '-'}</p>
                        <p><strong>Simulation:</strong> {selectedEmail?.status?.is_simulation ? 'Oui' : 'Non'}</p>
                      </div>

                      <div className="rounded-lg border p-3 bg-white">
                        <p className="font-semibold mb-2">Message</p>
                        <p><strong>Objet:</strong> {selectedEmail?.message_info?.subject || '-'}</p>
                        <p><strong>Catégorie:</strong> {selectedEmail?.message_info?.category || '-'}</p>
                        <p><strong>Modèle:</strong> {selectedEmail?.message_info?.template || '-'}</p>
                        <p><strong>Déclencheur:</strong> {selectedEmail?.message_info?.trigger || '-'}</p>
                        <p><strong>Mode:</strong> {selectedEmail?.message_info?.dispatch_mode || '-'}</p>
                        <p><strong>Admin manuel:</strong> {selectedEmail?.message_info?.manual_admin || '-'}</p>
                      </div>

                      <div className="rounded-lg border p-3 bg-white">
                        <p className="font-semibold mb-2">Chronologie</p>
                        <p><strong>Création:</strong> {formatDate(selectedEmail?.dates?.created_at)}</p>
                        <p><strong>Première tentative:</strong> {formatDate(selectedEmail?.dates?.first_attempt_at)}</p>
                        <p><strong>Dernière tentative:</strong> {formatDate(selectedEmail?.dates?.last_attempt_at)}</p>
                        <p><strong>Envoi:</strong> {formatDate(selectedEmail?.dates?.sent_at)}</p>
                        <p><strong>Livraison confirmée:</strong> {formatDate(selectedEmail?.dates?.delivery_confirmed_at)}</p>
                        <p><strong>Semaine planning:</strong> {selectedEmail?.message_info?.week_start || '-'}</p>
                      </div>
                    </div>

                    <div className="rounded-lg border p-3 bg-white text-xs" data-testid="email-detail-identifiers-panel">
                      <p className="font-semibold mb-2">Identifiants et erreurs</p>
                      <p><strong>ID interne:</strong> {selectedEmail?.identifiers?.internal_email_id || '-'}</p>
                      <p><strong>ID prestataire:</strong> {selectedEmail?.identifiers?.provider_message_id || '-'}</p>
                      <p><strong>Nombre de tentatives:</strong> {selectedEmail?.status?.attempts_count ?? 0}</p>
                      <p><strong>Erreur complète:</strong> {selectedEmail?.errors?.full_error || '-'}</p>
                      <p><strong>Raison rejet:</strong> {selectedEmail?.errors?.rejection_reason || '-'}</p>
                    </div>

                    <div className="rounded-lg border overflow-hidden" data-testid="email-detail-attempts-table">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="text-left p-2">Date tentative</th>
                            <th className="text-left p-2">ID e-mail</th>
                            <th className="text-left p-2">Statut</th>
                            <th className="text-left p-2">Erreur courte</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedEmail?.attempts || []).map((attempt, idx) => (
                            <tr key={`${attempt.email_id || idx}`} className="border-b last:border-b-0">
                              <td className="p-2">{formatDate(attempt.created_at)}</td>
                              <td className="p-2">{attempt.email_id || '-'}</td>
                              <td className="p-2">{attempt.status_label || '-'}</td>
                              <td className="p-2">{attempt.short_error || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="rounded-lg border p-3 bg-slate-50 text-xs" data-testid="email-detail-metadata-panel">
                      <p className="font-semibold mb-2">Métadonnées techniques (sanitisées)</p>
                      <pre className="whitespace-pre-wrap break-all text-[11px] leading-relaxed max-h-48 overflow-auto">{JSON.stringify(selectedEmail?.metadata || {}, null, 2)}</pre>
                    </div>
                  </>
                )}
              </div>

              <div className="border-t p-4 bg-gray-50 flex justify-end">
                <Button data-testid="email-detail-close-footer-button" variant="outline" onClick={() => setSelectedEmail(null)}>
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminEmails;
